<template>
  <div class="history-page pb-24">
    <!-- One page, season only: the week itself lives on Picks. -->
    <header class="history-hero">
      <div class="min-w-0">
        <p class="history-kicker">Results</p>
        <h1>Season <span>{{ season }}</span></h1>
        <p v-if="throughLine" class="history-sub">{{ throughLine }}</p>
      </div>
      <label class="season-picker">
        Season
        <select v-model.number="season" @change="load" class="control" aria-label="Season">
          <option v-for="year in seasonOptions" :key="year" :value="year">{{ year }}</option>
        </select>
      </label>
    </header>

    <div v-if="loading" class="py-24 text-center text-slate-500">Loading results…</div>
    <div v-else-if="error" class="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
      {{ error }}
    </div>

    <div v-else class="flex flex-col gap-3.5">
      <!-- Link an account to its standings row. -->
      <form
        v-if="needsPoolLink"
        @submit.prevent="savePoolName"
        class="flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-white/[0.12] px-4 py-3"
      >
        <p class="w-full text-xs text-slate-400">Link your name from the commissioner's standings PDF to see your own record.</p>
        <input v-model.trim="poolNameForm.first" placeholder="First name" class="control" required />
        <input v-model.trim="poolNameForm.last" placeholder="Last name" class="control" required />
        <button type="submit" class="rounded-lg bg-brand-green/15 px-3 py-2 text-xs font-black text-brand-green" :disabled="savingPoolName">
          {{ savingPoolName ? "Linking…" : "Link" }}
        </button>
        <span v-if="poolNameError" class="text-xs text-rose-300">{{ poolNameError }}</span>
      </form>

      <div v-if="!hasPool && poolIdentified" class="empty-state">
        <p class="font-bold text-white">No standings ingested for {{ season }} yet.</p>
        <p class="mt-1 text-sm text-slate-400">Your record fills in from the commissioner's weekly PDFs.</p>
      </div>

      <YouVsApp :games="games" :weeks="hasPool ? pool.weeks : []" :season="season" />

      <BookTrust :summary="seasonScorecard" />

      <!-- Every game of the season by line-value band. Collapsed: at a
           season's sample size a band's record is mostly noise. -->
      <details class="accordion">
        <summary>
          <span class="block">Does line value pay?</span>
          <span class="mt-0.5 block text-[11px] font-medium text-slate-400">Record by strength: Flat · Slight · Solid · Strong</span>
        </summary>
        <EdgeReportCard class="mt-2" title="" :bands="edgeBandsFor(games)" :yours="yourBandsFor(hasPool ? pool.weeks : [])" :has-data="hasScoredEdges" />
      </details>
    </div>
  </div>
</template>

<script>
import api from "@/utils/api";
import auth from "@/utils/auth";
import YouVsApp from "@/components/YouVsApp.vue";
import BookTrust from "@/components/BookTrust.vue";
import EdgeReportCard from "@/components/EdgeReportCard.vue";
import { analyzeHistoryGame, edgeBandSummaries, yourPickBands } from "@/utils/history.js";
import { bookScorecard } from "@/utils/bookScorecard.js";
import { isPerfect } from "@/utils/seasonScorecard.js";

function defaultSeason() {
  const now = new Date();
  // Before September the meaningful season is the one just finished.
  return now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
}

// Picks behind a sheet record: W+L+T, less the +2 a perfect week folds into
// its wins (7-0 is five picks).
function picksIn([wins, losses, ties = 0]) {
  const total = wins + losses + ties;
  return isPerfect([wins, losses, ties]) ? total - 2 : total;
}

export default {
  name: "History",
  components: { YouVsApp, BookTrust, EdgeReportCard },
  data() {
    return {
      season: defaultSeason(),
      games: [],
      pool: null,
      loading: true,
      error: null,
      poolNameForm: { first: "", last: "" },
      savingPoolName: false,
      poolNameError: null,
    };
  },
  computed: {
    seasonOptions() {
      // The pool's history starts in 2025; next season has no data until it kicks off.
      const now = new Date().getFullYear();
      return [now, now - 1, now - 2].filter((year) => year >= 2025);
    },
    poolIdentified() {
      return Boolean(this.pool && this.pool.identified !== false);
    },
    needsPoolLink() {
      return Boolean(this.pool && this.pool.identified === false);
    },
    hasPool() {
      return Boolean(this.pool && this.pool.weeks && this.pool.weeks.length);
    },
    // "Through Week 3 · 15 picks graded" -- your graded picks when linked,
    // otherwise just the last week with a settled game.
    throughLine() {
      // Counted off each week's sheet record, the same numbers the You tile
      // sums -- a week's individual picks can be missing while its record is in.
      const graded = this.hasPool ? this.pool.weeks.filter((w) => w.week_record) : [];
      if (graded.length) {
        const week = Math.max(...graded.map((w) => w.week));
        const picks = graded.reduce((n, w) => n + picksIn(w.week_record), 0);
        return `Through Week ${week} · ${picks} pick${picks === 1 ? "" : "s"} graded`;
      }
      const settled = this.games.filter((g) => g.ats_result).map((g) => g.week);
      return settled.length ? `Through Week ${Math.max(...settled)}` : null;
    },
    hasScoredEdges() {
      return this.games.some((g) => g.edge_result);
    },
    // From every game: a track record is the whole season.
    seasonScorecard() {
      return bookScorecard(this.games);
    },
  },
  watch: {
    "$route.query"(query) {
      if (this.redirectWeekLink(query)) return;
      const season = Number(query.season);
      if (season && season !== this.season) { this.season = season; this.load(); }
    },
  },
  mounted() {
    const q = this.$route.query;
    if (this.redirectWeekLink(q)) return;
    if (q.season) this.season = Number(q.season);
    this.load();
  },
  methods: {
    // The Week tab is gone: an old /history?scope=week&week=N link opens that
    // week on Picks, where the week now lives. ("last" is the scope's older name.)
    redirectWeekLink(query) {
      if (!["week", "last"].includes(query.scope) || !query.week) return false;
      this.$router.replace({
        path: "/picks",
        query: { season: String(query.season || this.season), week: String(query.week), season_type: "regular" },
      });
      return true;
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.get("/api/v1/history", {
          params: { season: this.season, season_type: "regular" },
        });
        this.games = data.games.map(analyzeHistoryGame);
        this.pool = data.pool || null;
        this.syncUrl();
      } catch (error) {
        this.games = [];
        this.pool = null;
        this.error = (error.response && error.response.data && error.response.data.error) ||
          "Could not load season results.";
      } finally {
        this.loading = false;
      }
    },
    syncUrl() {
      const query = { season: String(this.season) };
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    edgeBandsFor(games) {
      return edgeBandSummaries(games);
    },
    // The picker's own graded picks (the pool's verdicts in `pool.weeks`),
    // banded on the side picked. Null -- the card unchanged -- when unlinked.
    yourBandsFor(poolWeeks) {
      return yourPickBands(this.games, poolWeeks.filter(Boolean));
    },
    async savePoolName() {
      this.savingPoolName = true;
      this.poolNameError = null;
      try {
        await auth.updateProfile({
          pool_first_name: this.poolNameForm.first,
          pool_last_name: this.poolNameForm.last,
        });
        await this.load();
      } catch (error) {
        this.poolNameError = (error.response && error.response.data && error.response.data.error) || "Could not save.";
      } finally {
        this.savingPoolName = false;
      }
    },
  },
};
</script>

<style scoped>
.history-page { padding-top: 1.75rem; }
.history-hero { margin-bottom: 1.25rem; display: flex; align-items: flex-end; justify-content: space-between; gap: 0.75rem; }
.history-kicker { color: #94a3b8; font-size: 9px; font-weight: 700; letter-spacing: 0.25em; text-transform: uppercase; }
.history-hero h1 { margin-top: 0.5rem; color: white; font-size: 1.875rem; font-weight: 900; letter-spacing: -0.045em; line-height: 1.1; }
.history-hero h1 span { color: #34ef93; }
.history-sub { margin-top: 0.375rem; color: #94a3b8; font-size: 0.75rem; }
.season-picker { flex: none; display: flex; flex-direction: column; gap: 0.25rem; color: #94a3b8; font-size: 9px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; }
.control {
  border: 1px solid rgb(71 85 105 / 0.65);
  border-radius: 0.65rem;
  background: rgb(30 41 59 / 0.55);
  padding: 0.45rem 0.6rem;
  color: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 700;
  outline: none;
}
.control:focus { border-color: rgb(52 239 147 / 0.55); }
.season-picker .control { height: 2.75rem; border-radius: 0.75rem; color: white; font-size: 0.875rem; font-weight: 800; letter-spacing: 0; }
select.control {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 1.75rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23e2e8f0' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  background-size: 0.7rem;
}
.empty-state { border: 1px dashed rgb(255 255 255 / 0.12); border-radius: 0.9rem; padding: 2rem 1rem; text-align: center; }
.accordion {
  border: 1px solid rgb(255 255 255 / 0.1);
  border-radius: 1.25rem;
  background: rgb(1 16 38 / 0.45);
  padding: 0.875rem;
}
.accordion > summary {
  cursor: pointer;
  list-style: none;
  position: relative;
  padding-right: 1.5rem;
  font-size: 0.875rem;
  font-weight: 800;
  color: #e2e8f0;
}
.accordion > summary::-webkit-details-marker { display: none; }
.accordion > summary::after { content: "▾"; position: absolute; right: 0.25rem; top: 50%; transform: translateY(-50%); color: #94a3b8; transition: transform 150ms ease; }
.accordion[open] > summary::after { transform: translateY(-50%) rotate(180deg); }
.accordion :deep(.chart-card) { border: 0; border-radius: 0; background: transparent; padding: 0.45rem 0 0; box-shadow: none; }
@media (min-width: 640px) {
  .history-page { padding-top: 2rem; }
  .history-hero h1 { font-size: 2.25rem; }
}
</style>
